import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@fieldcheck/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Escrituras local-first que NO pueden perderse en silencio ───────────────
 *
 * Un `UPDATE … WHERE id = ?` de Drizzle contra la **vista** de PowerSync puede
 * casar **CERO filas y devolver «éxito»**. No es una hipótesis: está medido en
 * `space-academy-app` (mismo canon, tabla `activity` del stack local, curso
 * `06a131d0`) — el curso se crea, 1,03 s después se crea la unidad y **entre
 * medias NO existe ninguna op `course/updated`**: el título que el autor acababa
 * de escribir nunca llegó a generar una operación de CRUD.
 *
 * El mecanismo: cuando la cola de CRUD se vacía, PowerSync **reconstruye la
 * vista local desde el último checkpoint**. Si el checkpoint todavía no trae la
 * fila recién creada, la fila está **AUSENTE durante ese instante** — y un
 * UPDATE contra ella no escribe nada, no genera op y **no falla**.
 *
 * En FieldCheck el daño es peor que en otros clones: las mutaciones tocadas
 * aquí son partes de inspección, hallazgos y checklists que el operario de
 * campo cree haber guardado — y `template`/`inspection`/`finding` son EVIDENCIA
 * (compliance, soft-delete inmutable, ver `fieldwork/mutations.ts`). Perder en
 * silencio el título de un hallazgo o el estado de una inspección no es solo
 * un dato feo: es un registro de cumplimiento que nunca existió.
 *
 * Por qué la ausencia de error no prueba absolutamente nada aquí: el write-path
 * del servidor devuelve **2xx siempre a propósito** (un 4xx bloquearía la cola
 * del cliente para siempre, ver `infra/PENDING_WORK.md` §1.5.7). Aquel caso es
 * una vía DISTINTA —allí el servidor rechaza y calla—, pero el criterio es el
 * mismo: una escritura del usuario que desaparece sin rastro es el peor modo de
 * fallo.
 *
 * Contrato de este módulo: se escribe, se **RELEE**, se compara. Si el valor no
 * aterrizó se reintenta con backoff; si al agotarse sigue sin aterrizar, se
 * **LANZA** — el llamante lo comunica en vez de dejar al usuario mirando un
 * campo que no representa nada guardado.
 *
 * ── Lo que este módulo NO cubre (deliberado) ────────────────────────────────
 * Los `DELETE` duros no se verifican: su comprobación natural sería «la fila ya
 * no está», y «ausente» es justo el estado ambiguo de esta ventana — no se
 * puede distinguir «se borró» de «aún no había llegado». En FieldCheck además
 * casi no hay `DELETE` duro: `template`/`inspection`/`finding` son SOFT-DELETE
 * (papelera de evidencia, nunca se destruyen); los `.delete()` reales que
 * quedan (`deleteSiteMutation`, `deleteTemplateItemMutation`,
 * `deleteFieldworkAttachmentMutation`) borran auxiliares sin traza de
 * cumplimiento. Señal que reabriría esto: una purga que se pierda de verdad
 * (fila que reaparece en la papelera tras purgar).
 *
 * ── `scope`: aislamiento de inquilino, no solo corrección ───────────────────
 * Traído de `comandero-app` (`f8f1049`) y `listto-app` lo aloja para los
 * clones multi-org — FieldCheck es uno de ellos (`infra/sync_streams.yaml`,
 * stream `org_fieldwork`: sincroniza TODAS las organizaciones de las que el
 * usuario es `member`, no solo la activa). **No se activa en la conversión de
 * este repo**: a diferencia de `comandero`, ninguna mutación de FieldCheck
 * filtraba por `organization_id` en el `WHERE` antes de esta conversión (solo
 * `eq(id)`) — no hay un filtro de inquilino existente que la conversión
 * mecánica pudiera tirar. Añadir `scope` aquí sería una frontera de
 * autorización NUEVA (les faltaría plumbing de `organizationId` activo en
 * cada `update*Mutation` y sus 3 call-sites en `apps/web/src/features/**`),
 * y el server (`SyncRepository`) ya re-valida permisos en la subida — es
 * decisión de la campaña de autorización (`RBAC_AUTHZ_HARDENING`), no de
 * §4.11. Queda ALOJADA aquí, lista para cuando se decida esa frontera.
 *
 * ── TRES piezas sobre `updated_at`, y son TRES cosas distintas ──────────────
 * Se solapan en el nombre de la columna pero no en el comportamiento — cada
 * una tiene su propio flag, y ninguna implica las otras:
 *
 *   1. **Estampado** (`hasUpdatedAt`, por defecto `true`): si este helper
 *      escribe `updated_at` en el `SET` de cada intento. Todas las tablas que
 *      protege este repo (`site`, `template`, `template_item`, `inspection`,
 *      `inspection_answer`, `finding`, `user`) SÍ tienen `updated_at`
 *      (verificado contra `packages/powersync/src/schema.ts`), así que ninguna
 *      mutación de FieldCheck necesita `hasUpdatedAt: false` hoy — el flag
 *      queda alojado para tablas futuras que no la tengan.
 *   2. **Ignorar el `updated_at` que traiga el `patch`** (siempre, sin flag):
 *      nunca se compara ni se deja pasar al SET tal cual.
 *   3. **Verificar POR `updated_at`** (`verifyBy: "updated_at"`, opt-in): usar
 *      el sello estampado como prueba de aterrizaje en vez de comparar el
 *      patch por valor. Ninguna mutación de FieldCheck usa incrementos
 *      atómicos SQL hoy, así que tampoco tiene llamante — alojado igual.
 *
 * ── Por qué NO hay `patchRowsVerified` / `waitForRowsPresent` (por lotes) ───
 * `folio-office-app` y `apunte-digital-app` las añadieron para mover una
 * cohorte de filas de una vez (`WHERE id IN (...)`). FieldCheck no tiene
 * ninguna mutación así (verificado: sin `inArray` en
 * `packages/shared/src/fieldwork/mutations.ts` ni en `users/mutations.ts`) —
 * traerlas sería código sin llamante. Señal que reabriría esto: una papelera
 * por cohorte (restaurar varias inspecciones/hallazgos de una vez).
 *
 * ── Segunda clase: el upsert que decide INSERT-vs-UPDATE con un SELECT propio ─
 *
 * `patchRowVerified`/`waitForRowPresent` protegen la escritura contra una fila
 * cuyo `id` YA se conoce. Hay una clase distinta que ninguna de las dos cubre:
 * un upsert que decide si INSERTa o UPDATEa con un SELECT propio, porque no hay
 * `id` que releer hasta que esa rama está decidida. Ese SELECT cae en la MISMA
 * ventana del checkpoint: si la fila existe pero acaba de reaparecer tras un
 * rebuild, el SELECT la ve ausente y el código INSERTa una fila nueva — no se
 * pierde el dato, se DUPLICA la fila, y sin ningún error visible.
 *
 * **FieldCheck NO tiene esta clase** (comprobado, no asumido: `git grep -n -B8
 * '\.insert(' packages/shared/src/**\/mutations.ts` no encuentra ningún SELECT
 * decisorio antes de una rama insert/update — todas las `create*Mutation`
 * generan `id` con `generateUUID()` e insertan directo; la única mutación que
 * podría parecerlo, `upsertAnswerMutation`, ya recibe `existingId` EXPLÍCITO
 * del llamante — la UI mantiene el mapa de respuestas en memoria y sabe si ya
 * existe una fila, así que no hay SELECT decisorio que proteger). Se aloja
 * `readMatchingRowVerified` igual que en el canon, para el clon que sí aterrice
 * en esta forma. La guía completa (con el criterio de cuándo usar cada camino)
 * está en `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §3.
 */

/**
 * Backoff de los reintentos. Suma ≈ 6,05 s: cubre de sobra la ida y vuelta de
 * un checkpoint (medida en ~1 s contra el stack local) sin dejar una
 * interacción rápida —completar un ítem, cerrar un hallazgo— colgada
 * eternamente si la fila de verdad no existe.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Condición adicional de igualdad — aislamiento de inquilino en profundidad. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Esperas entre reintentos. Se expone para que los tests no duren segundos. */
  backoffMs?: readonly number[];
  /**
   * Condiciones extra que la fila TAMBIÉN debe cumplir, además de `id` —
   * típicamente `organization_id`. Se aplican tanto al UPDATE como a la
   * relectura: una fila que no cumple el scope se trata igual que una fila
   * ausente (ver nota de seguridad arriba). Alojado; ninguna mutación de este
   * repo lo usa hoy (ver nota arriba).
   */
  scope?: readonly ScopeCondition[];
  /**
   * Si la tabla tiene columna `updated_at`. Por defecto `true` (el
   * comportamiento de siempre: se estampa en cada intento). Ponlo a `false`
   * en tablas que NO tengan esa columna — con `false`, `patchRowVerified`
   * nunca la mete en el `SET`, y `verifyBy: "updated_at"` deja de ser válido.
   */
  hasUpdatedAt?: boolean;
  /**
   * Cómo verificar que la escritura aterrizó, cuando el patch no se puede
   * comparar por valor (p.ej. una expresión SQL de incremento atómico).
   * Modo EXPLÍCITO, nunca automático. `"updated_at"` compara el sello que
   * este helper estampa en cada intento: prueba que ESTA escritura aterrizó,
   * no que el valor final sea el esperado. Requiere `hasUpdatedAt` distinto
   * de `false`.
   */
  verifyBy?: "updated_at";
}

/** Error de una escritura local-first que no llegó a aterrizar en la vista. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Construye `eq(id) AND eq(scope1) AND …` — o solo `eq(id)` sin scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Lee la fila por id (+ `scope`, si se da), o `undefined` si no está en la
 * vista local o no cumple el scope — mismo resultado que "fila ausente", a
 * propósito (ver nota de seguridad arriba).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle no puede inferir la forma de una `SQLiteTable` genérica: el select
  // resuelve a `{ [x: string]: any }[]`, así que anotar el destino basta —no
  // hace falta pasar por `unknown`, eso sería estrechar sin comprobación real.
  const rows: Record<string, unknown>[] = await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1);
  return rows[0];
}

/** `null` y `undefined` son el mismo «vacío» al comparar contra SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * `UPDATE … WHERE id = ?` **verificado**: escribe, relee, compara los campos
 * del patch y reintenta mientras no hayan aterrizado. Lanza `LocalWriteLostError`
 * si al agotar el backoff siguen sin estar.
 *
 * Si `options.hasUpdatedAt` no es `false` (el caso normal), `updated_at` lo
 * pone esta función en cada intento — cualquier `updated_at` que traiga el
 * propio `patch` se ignora SIEMPRE, tanto al escribir como al comparar; el
 * resto del `patch` debe traer solo campos de dominio. Con `hasUpdatedAt:
 * false` el `SET` no la toca en absoluto. Con `options.verifyBy ===
 * "updated_at"` no se compara el patch por valor: se comprueba que el sello
 * de ESTE intento aterrizó — combinarlo con `hasUpdatedAt: false` es un error
 * de configuración y lanza de inmediato, antes de escribir nada.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" no es compatible con hasUpdatedAt: false — la tabla no tiene esa columna que comparar.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Espera a que la fila EXISTA en la vista local antes de tocarla (+ `scope`,
 * si se da, con el mismo criterio de aislamiento que `patchRowVerified`).
 *
 * Para las mutaciones que no son un patch simple —en este repo,
 * `restoreTemplateVersionMutation`, que dentro de UNA transacción actualiza
 * `template` y RECREA su set de `template_item`. Ahí no vale reintentar
 * dentro de la transacción (se dormiría con el lock de escritura tomado), y
 * si `templateId` no está en la vista todavía el `UPDATE` no casa nada pero
 * el `DELETE`+`INSERT` de ítems sí ocurre: la plantilla queda con los ítems
 * de la versión restaurada pero el `name`/`description` viejos — mezcla
 * inconsistente, no solo un campo perdido.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Relee, con el mismo backoff que el resto del módulo, una fila que se
 * identifica por CONTENIDO en vez de por `id` — la Clase 2 del catálogo (ver
 * la nota "Segunda clase" arriba). **FieldCheck no tiene hoy ningún llamante
 * para esto** (verificado, no asumido — ver nota arriba): vive aquí ALOJADA,
 * copiada tal cual del canon (`listto-app`, traída originalmente de
 * `stockpilot-app@b8c4bc8a`), para el día en que una mutación de este repo
 * decida INSERT-vs-UPDATE por contenido en vez de por `id` conocido.
 *
 * Se llama SIEMPRE antes de abrir la transacción que decide INSERT-vs-UPDATE,
 * y NUNCA dentro de ella — mismo motivo que `waitForRowPresent`. Cuando hay
 * varias líneas que resolver, resuélvelas en PARALELO (`Promise.all`) antes
 * de la transacción.
 *
 * A diferencia de `patchRowVerified`/`waitForRowPresent`, agotar el backoff
 * NO es un error aquí: la ausencia genuina (primera vez que se toca esa
 * combinación de contenido) es un resultado VÁLIDO, así que esta función
 * devuelve `undefined` en vez de lanzar, y el llamante procede a INSERTar
 * exactamente como si no hubiera reintentado.
 *
 * `read` debe repetir la MISMA consulta en cada intento — una función que
 * vuelve a construir el `where` sobre `db`/`trx`, no una promesa ya resuelta
 * que se reutilizaría sin volver a tocar la base.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
